import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  emptyInitialState,
  initialReview,
  RatingValue,
  ReviewMap,
  useReviewFormReducer,
} from './hooks/use-review-form-reducer';
import { Orders_GetOrderFieldsFragment } from '@/types';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { ShipmentChecklist } from './shipment-checklist';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { useLayoutContext } from '@/contexts/layout-context';
import { useRateOrder } from './hooks/use-rate-order';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  displayItemPrices: boolean;
  onClose: () => void;
  onOrderRated: () => void;
  order: Orders_GetOrderFieldsFragment;
  useInvoicePrices: boolean;
}

export const OrderReviewActionSheet: React.FC<Props> = ({
  onClose,
  onOrderRated,
  order,
  useInvoicePrices,
  displayItemPrices,
}) => {
  const windowDimensions = useWindowDimensions();
  const { isSmallLayout } = useLayoutContext();
  const { rateOrder, loading: isSubmitting } = useRateOrder();
  const { showErrorToast } = useToasts();

  const orderItems = useMemo(() => order.orders_items || [], [order]);
  const initialReviewState = useMemo(() => {
    // Initialize review state with a thumbs up for all order items
    return {
      ...emptyInitialState,
      reviews: orderItems.reduce<ReviewMap>((acc, orderItem) => {
        acc[orderItem.uuid] = {
          ...initialReview,
          rating: RatingValue.ThumbsUp,
        };
        return acc;
      }, {}),
    };
  }, [orderItems]);

  const [reviewState, dispatch] = useReviewFormReducer(initialReviewState);

  const submitReview = async () => {
    Analytics.track('order_review.submit', {
      orderUUID: order.uuid,
    });

    try {
      await rateOrder(reviewState);
      onOrderRated();
    } catch (e) {
      showErrorToast();
    }
  };

  const displayItemCode =
    order.supplier.supplier_metadata?.display_item_code ?? false;
  const title = i18n.t('order_review');
  return (
    <BaseActionSheet
      contentStyle={[
        styles.actionSheetContent,
        { height: Math.floor(windowDimensions.height * 0.85) },
      ]}
      isVisible
      onClose={onClose}
      title={title}
    >
      <View style={styles.heading}>
        {isSmallLayout ? (
          <Text style={[TYPOGRAPHY.h2, styles.title]}>{title}</Text>
        ) : null}
      </View>
      <ShipmentChecklist
        dispatch={dispatch}
        displayItemCode={displayItemCode}
        displayItemPrices={displayItemPrices}
        isSubmitting={isSubmitting}
        onDonePressed={submitReview}
        orderItems={orderItems}
        orderUUID={order.uuid}
        reviewState={reviewState}
        useInvoicePrices={useInvoicePrices}
      />
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  actionSheetContent: {
    paddingBottom: 0,
    paddingHorizontal: 0,
  },
  heading: {
    marginHorizontal: SCREEN_PADDING,
  },
  title: {
    marginBottom: 8,
  },
});
